/* eslint-disable max-lines */
import { useCreateSOP, useUpdateSOP } from '@hakimo-ui/hakimo/data-access';
import { SOPWorkflow } from '@hakimo-ui/hakimo/types';
import {
  toast,
  withErrorBoundary,
  useCanUpdateSOP,
} from '@hakimo-ui/hakimo/util';
import { Alert, Button, Selectable } from '@hakimo-ui/shared/ui-base';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { AddSOPModal } from './add-sop-modal/AddSOPModal';
import SOPAlarmTypeSelector from './sop-alarm-type-selector/SOPAlarmTypeSelector';
import SOPNotesCard from './sop-cards/SOPNotesCard';
import SOPSiteInfoCard from './sop-cards/SOPSiteInfoCard';
import SOPExceptionsCard from './sop-cards/SOPExceptionsCard';
import SOPSituationsCard from './sop-cards/SOPSituationsCard';
import SOPQuickResolveCard from './sop-cards/SOPQuickResolveCard';
import SOPTalkdownCard from './sop-cards/SOPTalkdownCard';
import SOPEscalationsCard from './sop-cards/SOPEscalationsCard';
import {
  createCreateSOPPayload,
  createUpdateSOPPayload,
  getInitialData,
  updateNullValues,
} from './utils';
import { SITE_CATEGORY_OPTIONS } from './constants';
import { validateFormData } from './validation';

interface Props {
  sopId: string;
  sopWorkflow?: SOPWorkflow;
  showTitle?: boolean;
  locationId?: string;
  tenantId?: string;
  alarmTypeId?: string;
  onAlarmTypeChange?: (alarmType: Selectable | null) => void;
  sopAlarmTypes?: Selectable[];
  timezone?: string;
  fullWidth?: boolean;
  compact?: boolean;
}

export function SOPManager(props: Props) {
  const {
    sopWorkflow,
    sopId,
    showTitle = true,
    locationId,
    tenantId,
    alarmTypeId,
    onAlarmTypeChange,
    sopAlarmTypes,
    timezone,
    fullWidth = false,
    compact = false,
  } = props;
  const canUpdateSOP = useCanUpdateSOP();
  const [isEditing, setIsEditing] = useState(false);
  const [isAddSOPModalOpen, setIsAddSOPModalOpen] = useState(false);
  const [updatedSOPWorkflow, setUpdatedSOPWorkflow] = useState<
    SOPWorkflow | undefined
  >(() => (sopWorkflow ? updateNullValues(sopWorkflow, timezone) : undefined));
  const [validationState, setValidationState] = useState({
    isValid: true,
    message: '',
  });
  const validationAlertRef = useRef<HTMLDivElement>(null);
  const mutationAlertRef = useRef<HTMLDivElement>(null);
  const onUpdateSuccess = () => {
    setIsEditing(false);
    toast('SOP workflow updated successfully', { type: 'success' });
  };

  const onCreateSuccess = () => {
    setIsEditing(false);
    toast('SOP workflow created successfully', { type: 'success' });
  };

  const updateMutation = useUpdateSOP(sopId, onUpdateSuccess);
  const createMutation = useCreateSOP(onCreateSuccess);

  useEffect(() => {
    if (!isEditing) {
      sopWorkflow &&
        setUpdatedSOPWorkflow(updateNullValues(sopWorkflow, timezone));
    }
  }, [isEditing, sopWorkflow, timezone]);

  useEffect(() => {
    if (updateMutation.isError || createMutation.isError) {
      setTimeout(() => {
        mutationAlertRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    }
  }, [updateMutation.isError, createMutation.isError]);

  const toggleEditing = () => {
    setIsEditing((prev) => {
      if (prev && !sopWorkflow) {
        setUpdatedSOPWorkflow(undefined);
      }
      return !prev;
    });
    setValidationState({ isValid: true, message: '' });
  };
  const onCancelEdit = () => {
    setIsEditing(false);
    if (!sopWorkflow) {
      setUpdatedSOPWorkflow(undefined);
    }
    setValidationState({ isValid: true, message: '' });
  };
  const openAddSOPModal = () => setIsAddSOPModalOpen(true);
  const closeAddSOPModal = () => setIsAddSOPModalOpen(false);

  const handleAlarmTypeChange = (alarmType: Selectable | null) => {
    if (onAlarmTypeChange) {
      onAlarmTypeChange(alarmType);
    }
  };

  const handleSOPCreated = (alarmType: Selectable) => {
    handleAlarmTypeChange(alarmType);
  };

  const onSubmit = () => {
    const { isValid, message } = validateFormData(updatedSOPWorkflow);

    if (isValid) {
      setValidationState({ isValid, message: '' });
    } else {
      setValidationState({ isValid, message: message || '' });
      setTimeout(() => {
        validationAlertRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
      return;
    }

    if (!sopWorkflow) {
      // Create new SOP
      if (!locationId || !tenantId) {
        toast('Missing location or tenant information', { type: 'error' });
        return;
      }
      const createPayload = createCreateSOPPayload(
        updatedSOPWorkflow!,
        tenantId,
        locationId,
        alarmTypeId
      );
      createMutation.mutate(createPayload);
    } else {
      // Update existing SOP
      const updatePayload = createUpdateSOPPayload(
        updatedSOPWorkflow!,
        alarmTypeId,
        !alarmTypeId
      );
      updateMutation.mutate(updatePayload);
    }
  };

  const enableWorkflow = () => {
    const initData = getInitialData();
    setUpdatedSOPWorkflow(initData);
    setIsEditing(true);
  };

  const renderSOPHeader = () => {
    if (!showTitle) return null;

    return (
      <div className="mb-2 flex items-center justify-between p-2">
        <div className="flex items-center gap-4">
          <span className="font-bold">
            Standard Operating Procedure (SOP) Workflow
          </span>
          {!isEditing && (
            <SOPAlarmTypeSelector
              onChange={handleAlarmTypeChange}
              initialAlarmTypeId={alarmTypeId}
              sopAlarmTypes={sopAlarmTypes}
            />
          )}
          {!isEditing && canUpdateSOP && (
            <Button onClick={toggleEditing} variant="icon">
              <span className="flex gap-2">
                <PencilSquareIcon className="h-5 w-5" />
                Edit
              </span>
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isEditing && (
            <>
              <Button
                variant="primary"
                onClick={onSubmit}
                loading={updateMutation.isLoading || createMutation.isLoading}
              >
                Submit
              </Button>
              <Button onClick={toggleEditing} variant="outline">
                Cancel
              </Button>
            </>
          )}
          {!isEditing &&
            locationId &&
            tenantId &&
            sopWorkflow &&
            canUpdateSOP && (
              <Button onClick={openAddSOPModal} variant="outline">
                Add/Modify SOP
              </Button>
            )}
        </div>
      </div>
    );
  };

  if (!sopWorkflow && !updatedSOPWorkflow) {
    return (
      <div className="w-full p-2">
        <div className="mt-16 flex flex-col items-center justify-center gap-4">
          <div>SOP workflow is not enabled for this location.</div>
          {showTitle && canUpdateSOP && (
            <Button variant="primary" onClick={enableWorkflow}>
              Enable Workflow
            </Button>
          )}
        </div>
      </div>
    );
  }

  const currentSOPWorkflow =
    updatedSOPWorkflow ||
    (sopWorkflow ? updateNullValues(sopWorkflow, timezone) : getInitialData());

  return (
    <div className="w-full">
      {renderSOPHeader()}
      {isAddSOPModalOpen && locationId && tenantId && (
        <AddSOPModal
          isOpen={isAddSOPModalOpen}
          onClose={closeAddSOPModal}
          locationId={locationId}
          tenantId={tenantId}
          sopAlarmTypes={sopAlarmTypes}
          onSOPCreated={handleSOPCreated}
        />
      )}
      {(updateMutation.isError || createMutation.isError) && (
        <div ref={mutationAlertRef}>
          <Alert type="error">
            {updateMutation.error?.message || createMutation.error?.message}
          </Alert>
        </div>
      )}
      {!validationState.isValid && (
        <div ref={validationAlertRef}>
          <Alert type="warning">{validationState.message}</Alert>
        </div>
      )}
      <div
        className={
          fullWidth
            ? 'bg-onlight-bg-1 dark:bg-ondark-bg-1'
            : 'bg-hakimo-light-bg dark:bg-hakimo-dark-bg flex justify-center'
        }
      >
        <div className="w-full max-w-4xl">
          <div className="flex flex-col gap-4 rounded-lg">
            {currentSOPWorkflow && (
              <div
                className={`${
                  fullWidth ? 'mt-1 gap-2' : 'mt-4 gap-4'
                } flex flex-col`}
              >
                {!compact && (
                  <SOPSiteInfoCard
                    siteAddress={currentSOPWorkflow.siteAddress || ''}
                    siteGoogleMapLocation={
                      currentSOPWorkflow.siteGoogleMapLocation || ''
                    }
                    siteCategory={currentSOPWorkflow.siteCategory || ''}
                    siteDescription={currentSOPWorkflow.siteDescription || ''}
                    businessHours={
                      currentSOPWorkflow.businessHours || {
                        hours: '',
                        timezone: '',
                      }
                    }
                    isZeroTolerance={
                      currentSOPWorkflow.isZeroTolerance || false
                    }
                    isLongRangeAnalysisEnabled={
                      currentSOPWorkflow.isLongRangeAnalysisEnabled || false
                    }
                    siteCategoryOptions={SITE_CATEGORY_OPTIONS}
                    onChange={(data) =>
                      setUpdatedSOPWorkflow({
                        ...currentSOPWorkflow,
                        ...data,
                      })
                    }
                    isEditing={isEditing}
                    timezone={timezone}
                  />
                )}
                <SOPSituationsCard
                  compact={compact}
                  situations={currentSOPWorkflow.situations || []}
                  onChange={(situations) =>
                    setUpdatedSOPWorkflow({
                      ...currentSOPWorkflow,
                      situations,
                    })
                  }
                  isEditing={isEditing}
                  locationId={locationId}
                />
                <SOPExceptionsCard
                  compact={compact}
                  exceptions={currentSOPWorkflow.exceptions || []}
                  onChange={(exceptions) =>
                    setUpdatedSOPWorkflow({
                      ...currentSOPWorkflow,
                      exceptions,
                    })
                  }
                  isEditing={isEditing}
                  locationId={locationId}
                />
                <SOPNotesCard
                  compact={compact}
                  notes={currentSOPWorkflow.notes || []}
                  onChange={(notes) =>
                    setUpdatedSOPWorkflow({
                      ...currentSOPWorkflow,
                      notes,
                    })
                  }
                  isEditing={isEditing}
                  locationId={locationId}
                />
                {!compact && (
                  <SOPQuickResolveCard
                    quickResolveActions={
                      currentSOPWorkflow.quickResolveActions || []
                    }
                    onChange={(quickResolveActions) =>
                      setUpdatedSOPWorkflow({
                        ...currentSOPWorkflow,
                        quickResolveActions,
                      })
                    }
                    isEditing={isEditing}
                  />
                )}
                <SOPTalkdownCard
                  compact={compact}
                  isTalkdownEnabled={
                    currentSOPWorkflow.isTalkdownEnabled || false
                  }
                  talkdowns={currentSOPWorkflow.talkdowns || []}
                  talkdownNotes={currentSOPWorkflow.talkdownNotes || []}
                  onChange={(data) =>
                    setUpdatedSOPWorkflow({
                      ...currentSOPWorkflow,
                      ...data,
                    })
                  }
                  isEditing={isEditing}
                />
                <SOPEscalationsCard
                  compact={compact}
                  escalationProtocol={
                    currentSOPWorkflow.escalationProtocol || []
                  }
                  escalationPoints={currentSOPWorkflow.escalationPoints || []}
                  onProtocolChange={(escalationProtocol) =>
                    setUpdatedSOPWorkflow({
                      ...currentSOPWorkflow,
                      escalationProtocol,
                    })
                  }
                  onPointsChange={(escalationPoints) =>
                    setUpdatedSOPWorkflow({
                      ...currentSOPWorkflow,
                      escalationPoints,
                    })
                  }
                  isEditing={isEditing}
                />
                {!isEditing && <div />}
                {isEditing && (
                  <div className="flex gap-4 px-2 pb-4">
                    <Button
                      variant="primary"
                      onClick={onSubmit}
                      loading={
                        updateMutation.isLoading || createMutation.isLoading
                      }
                    >
                      Submit
                    </Button>
                    <Button onClick={onCancelEdit}>Cancel</Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default withErrorBoundary(SOPManager);
